import "ios-vibrator-pro-max";

import type React from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import {
  Search,
  Plus,
  Lightbulb,
  ArrowUp,
  Menu,
  PenSquare,
  RefreshCcw,
  Copy,
  Share2,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ModelSelector } from "./ModelSelector";
import type {
  Message,
  ActiveButton,
  MessageSection,
  StreamingWord,
  ChatInterfaceProps,
} from "@/lib/ChatModel";
import { DefaultPills, RenderMarkdown } from "./ChatHelpers";
import { Link } from "react-router";
import ThemeToggle from "../ThemeToggle";
import { ChatInputEditor } from "./ChatInputEditor";
import { RenderMessage } from "./RenderMessage";
import { WorkflowCard } from "../Workflow/WorkflowCard";
// Faster word delay for smoother streaming
const WORD_DELAY = 40; // ms per word
const CHUNK_SIZE = 2; // Number of words to add at once

export function ChatInterface({
  id,
  messages,
  onSubmit,
  selectedModel,
  onModelChange,
  title = "Chat",
  streamingWords,
}: ChatInterfaceProps) {
  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const newSectionRef = useRef<HTMLDivElement>(null);
  const [hasTyped, setHasTyped] = useState(false);
  const [activeButton, setActiveButton] = useState<ActiveButton>("none");
  const [isMobile, setIsMobile] = useState(false);
  const [messageSections, setMessageSections] = useState<MessageSection[]>([]);
  const [viewportHeight, setViewportHeight] = useState(0);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const shouldFocusAfterStreamingRef = useRef(false);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const isStreaming = useMemo(
    () => messages.some((m) => m.role === "assistant" && !m.completed),
    [messages],
  );
  // Store selection state
  const selectionStateRef = useRef<{
    start: number | null;
    end: number | null;
  }>({ start: null, end: null });

  // Constants for layout calculations to account for the padding values
  const HEADER_HEIGHT = 48; // 12px height + padding
  const INPUT_AREA_HEIGHT = 100; // Approximate height of input area with padding
  const TOP_PADDING = 48; // pt-12 (3rem = 48px)
  const BOTTOM_PADDING = 128; // pb-32 (8rem = 128px)
  const ADDITIONAL_OFFSET = 16; // Reduced offset for fine-tuning

  // Check if device is mobile and get viewport height
  useEffect(() => {
    const checkMobileAndViewport = () => {
      const isMobileDevice = window.innerWidth < 768;
      setIsMobile(isMobileDevice);

      // Capture the viewport height
      const vh = window.innerHeight;
      setViewportHeight(vh);

      // Apply fixed height to main container on mobile
      if (isMobileDevice && mainContainerRef.current) {
        mainContainerRef.current.style.height = `${vh}px`;
      }
    };

    checkMobileAndViewport();

    // Set initial height
    if (mainContainerRef.current) {
      mainContainerRef.current.style.height = isMobile
        ? `${viewportHeight}px`
        : "100svh";
    }

    // Update on resize
    window.addEventListener("resize", checkMobileAndViewport);

    return () => {
      window.removeEventListener("resize", checkMobileAndViewport);
    };
  }, [isMobile, viewportHeight]);

  useEffect(() => {
    // Only use regular messages for sections, handle streaming separately
    const allMessages = [...messages];
    if (allMessages.length === 0) {
      setMessageSections([]);
      return;
    }

    const sections: MessageSection[] = [];
    let currentSection: MessageSection = {
      id: `section-${Date.now()}-0`,
      messages: [],
      isNewSection: false,
      sectionIndex: 0,
    };

    for (const message of allMessages) {
      if (message.newSection) {
        // Start a new section
        if (currentSection.messages.length > 0) {
          sections.push({
            ...currentSection,
            isActive: false,
          });
        }

        const newSectionId = `section-${Date.now()}-${sections.length}`;
        currentSection = {
          id: newSectionId,
          messages: [message],
          isNewSection: true,
          isActive: true,
          sectionIndex: sections.length,
        };
      } else {
        currentSection.messages.push(message);
      }
    }

    if (currentSection.messages.length > 0) {
      sections.push(currentSection);
    }

    setMessageSections(sections);
  }, [messages]);

  // Scroll to maximum position when new section is created, but only for sections after the first
  useEffect(() => {
    if (messageSections.length > 1) {
      setTimeout(() => {
        const scrollContainer = chatContainerRef.current;

        if (scrollContainer) {
          // Scroll to maximum possible position
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [messageSections]);

  // Focus the textarea on component mount (only on desktop)
  useEffect(() => {
    if (textareaRef.current && !isMobile) {
      textareaRef.current.focus();
    }
  }, [isMobile]);

  // Set focus back to textarea after streaming ends (only on desktop)
  useEffect(() => {
    if (!isStreaming && shouldFocusAfterStreamingRef.current && !isMobile) {
      focusTextarea();
      shouldFocusAfterStreamingRef.current = false;
    }
  }, [isStreaming, isMobile]);

  // Calculate available content height (viewport minus header and input)
  const getContentHeight = () => {
    // Calculate available height by subtracting the top and bottom padding from viewport height
    return viewportHeight - TOP_PADDING - BOTTOM_PADDING - ADDITIONAL_OFFSET;
  };

  // Save the current selection state
  const saveSelectionState = () => {
    if (textareaRef.current) {
      selectionStateRef.current = {
        start: textareaRef.current.selectionStart,
        end: textareaRef.current.selectionEnd,
      };
    }
  };

  // Restore the saved selection state
  const restoreSelectionState = () => {
    const textarea = textareaRef.current;
    const { start, end } = selectionStateRef.current;

    if (textarea && start !== null && end !== null) {
      // Focus first, then set selection range
      textarea.focus();
      textarea.setSelectionRange(start, end);
    } else if (textarea) {
      // If no selection was saved, just focus
      textarea.focus();
    }
  };

  const focusTextarea = () => {
    if (textareaRef.current && !isMobile) {
      textareaRef.current.focus();
    }
  };

  const handleInputContainerClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    // Only focus if clicking directly on the container, not on buttons or other interactive elements
    if (
      e.target === e.currentTarget ||
      (e.currentTarget === inputContainerRef.current &&
        !(e.target as HTMLElement).closest("button"))
    ) {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    // Only allow input changes when not streaming
    if (!isStreaming) {
      setInputValue(newValue);

      if (newValue.trim() !== "" && !hasTyped) {
        setHasTyped(true);
      } else if (newValue.trim() === "" && hasTyped) {
        setHasTyped(false);
      }

      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = "auto";
        const newHeight = Math.max(24, Math.min(textarea.scrollHeight, 160));
        textarea.style.height = `${newHeight}px`;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isStreaming) {
      // Add vibration when message is submitted
      if ("vibrate" in navigator) {
        navigator.vibrate(50);
      }

      const userMessage = inputValue.trim();

      // Reset input before starting the AI response
      setInputValue("");
      setHasTyped(false);
      setActiveButton("none");

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      // Only focus the textarea on desktop, not on mobile
      if (!isMobile) {
        focusTextarea();
      } else {
        // On mobile, blur the textarea to dismiss the keyboard
        if (textareaRef.current) {
          textareaRef.current.blur();
        }
      }

      // Pass the message to parent component
      await onSubmit(userMessage);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle Cmd+Enter on both mobile and desktop
    if (!isStreaming && e.key === "Enter" && e.metaKey) {
      e.preventDefault();
      void handleSubmit(e);
      return;
    }

    // Only handle regular Enter key (without Shift) on desktop
    if (!isStreaming && !isMobile && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit(e);
    }
  };

  const toggleButton = (button: ActiveButton) => {
    if (!isStreaming) {
      // Save the current selection state before toggling
      saveSelectionState();

      setActiveButton((prev) => (prev === button ? "none" : button));

      // Restore the selection state after toggling
      setTimeout(() => {
        restoreSelectionState();
      }, 0);
    }
  };

  // Determine if a section should have fixed height (only for sections after the first)
  const shouldApplyHeight = (sectionIndex: number) => {
    return sectionIndex > 0;
  };

  return (
    <div
      ref={mainContainerRef}
      className="flex flex-col overflow-hidden"
      style={{
        height: isMobile ? `${viewportHeight}px` : "100svh",
      }}
    >
      <header className="bg-background fixed mx-20 top-0 left-0 right-0 h-12 flex items-center px-4 z-20">
        <div className="mx-auto flex items-center justify-center px-2">
          <h1 className="text-base font-medium">{title}</h1>
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full h-8 w-8"
            >
              <Link to={"/app/chat"} reloadDocument>
                <PenSquare className="h-5 w-5" />
                <span className="sr-only">New Chat</span>
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Chat container */}
      <div
        ref={chatContainerRef}
        className="flex-grow pt-12 px-4 overflow-y-auto"
      >
        <div className="max-w-3xl mx-auto space-y-4 pb-40">
          {messageSections.map((section, sectionIndex) => (
            <>
              {section.isNewSection ? (
                <div
                  key={section.id}
                  ref={
                    sectionIndex === messageSections.length - 1
                      ? newSectionRef
                      : null
                  }
                  style={
                    section.isActive && shouldApplyHeight(section.sectionIndex)
                      ? { height: `${getContentHeight()}px` }
                      : {}
                  }
                  className="pt-4 flex flex-col justify-start"
                >
                  {section.messages.map((message) => (
                    <>
                      <RenderMessage
                        key={message.id}
                        {...message}
                        streamingWords={streamingWords}
                      />
                      {id &&
                        messages.length > 1 &&
                        messages.every((m) => m.completed) &&
                        message.id === messages[messages.length - 1].id && (
                          <WorkflowCard id={id} />
                        )}
                    </>
                  ))}
                </div>
              ) : (
                <div
                  key={section.id}
                  ref={
                    sectionIndex === messageSections.length - 1
                      ? newSectionRef
                      : null
                  }
                >
                  {section.messages.map((message) => (
                    <>
                      <RenderMessage
                        key={message.id}
                        {...message}
                        streamingWords={streamingWords}
                      />
                      {id &&
                        messages.length > 1 &&
                        messages.every((m) => m.completed) &&
                        message.id === messages[messages.length - 1].id && (
                          <WorkflowCard id={id} />
                        )}
                    </>
                  ))}
                </div>
              )}
            </>
          ))}
        </div>
      </div>

      {/* Input container */}
      <div className="fixed bottom-0 left-0 right-0 p-4">
        {messages.length === 0 && (
          <DefaultPills
            layout="horizontal"
            onSelect={(pill) => {
              setInputValue(pill);
            }}
            pills={[
              `I want to build a personal finance tracker that queries my expenses from Google Sheets, and compares against my financial goals (also in another google sheet)
Every weekend, I want a report of the weeks expenses, and how it affects my long term goals.
`,
              `I want a weekly report of company's expenses, and I want it sent to my email.
`,
            ]}
          />
        )}
        <form
          onSubmit={handleSubmit}
          className="max-w-3xl mx-auto  bg-background"
        >
          <div
            ref={inputContainerRef}
            className={cn(
              "relative w-full rounded-3xl border border-gray-200 p-3 cursor-text",
              isStreaming && "opacity-80",
            )}
            onClick={handleInputContainerClick}
            onKeyDown={(e) => handleInputContainerClick(e)}
          >
            <div className="pb-9">
              <ChatInputEditor
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isStreaming}
                ref={textareaRef}
                isStreaming={isStreaming}
              />
            </div>

            <div className="absolute bottom-3 left-3 right-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className={cn(
                      "rounded-full h-8 w-8 flex-shrink-0 border-gray-200 p-0 transition-colors",
                      activeButton === "add" && "bg-gray-100 border-gray-300",
                    )}
                    onClick={() => toggleButton("add")}
                    disabled={isStreaming}
                  >
                    <Plus
                      className={cn(
                        "h-4 w-4 text-faded",
                        activeButton === "add" && "text-secondary",
                      )}
                    />
                    <span className="sr-only">Add</span>
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "rounded-full h-8 px-3 flex items-center border-gray-200 gap-1.5 transition-colors",
                      activeButton === "deepSearch" &&
                        "bg-gray-100 border-gray-300",
                    )}
                    onClick={() => toggleButton("deepSearch")}
                    disabled={isStreaming}
                  >
                    <Search
                      className={cn(
                        "h-4 w-4 text-gray-500",
                        activeButton === "deepSearch" && "text-gray-700",
                      )}
                    />
                    <span
                      className={cn(
                        "text-gray-900 text-sm",
                        activeButton === "deepSearch" && "font-medium",
                      )}
                    >
                      DeepSearch
                    </span>
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "rounded-full h-8 px-3 flex items-center border-gray-200 gap-1.5 transition-colors",
                      activeButton === "think" && "bg-gray-100 border-gray-300",
                    )}
                    onClick={() => toggleButton("think")}
                    disabled={isStreaming}
                  >
                    <Lightbulb
                      className={cn(
                        "h-4 w-4 text-gray-500",
                        activeButton === "think" && "text-gray-700",
                      )}
                    />
                    <span
                      className={cn(
                        "text-gray-900 text-sm",
                        activeButton === "think" && "font-medium",
                      )}
                    >
                      Think
                    </span>
                  </Button>
                </div>

                <div className="flex gap-2 items-center">
                  {onModelChange && (
                    <ModelSelector
                      selectedModel={selectedModel}
                      onModelChange={onModelChange}
                      disabled={isStreaming}
                    />
                  )}
                  <Button
                    type="submit"
                    variant="outline"
                    size="icon"
                    className={cn(
                      "rounded-full h-8 w-8 border-0 flex-shrink-0 transition-all duration-200",
                      hasTyped ? "bg-black scale-110" : "bg-gray-200",
                    )}
                    disabled={!inputValue.trim() || isStreaming}
                  >
                    <ArrowUp
                      className={cn(
                        "h-4 w-4 transition-colors",
                        hasTyped ? "text-white" : "text-gray-500",
                      )}
                    />
                    <span className="sr-only">Submit</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
